import NeptuneElement from "../../NeptuneElement.js";

const NAV_KEYS = ['ArrowDown', 'Down', 'ArrowUp', 'Up', 'Home', 'End', 'Escape'];

class MenuItem extends NeptuneElement {

    templateMarkup = `
        <li>
            <slot></slot>
        </li>
        <hr hidden>
    `;

    componentCssUrl = '/src/components/navigation/menu/menu-item.css';

    /**
     * Lists attributes that trigger attributeChangedCallback.
     * @returns {string[]}
     */
    static get observedAttributes() {
        return ['disabled', 'divider'];
    }

    /**
     * Initializes the component after the base render lifecycle completes.
     * @returns {Promise<void>}
     */
    async connectedCallback() {
        await super.connectedCallback();
        this.setAttribute('tabindex', '-1');
        this.processInitialAttributesAfterRender(MenuItem.observedAttributes);
    }

    /**
     * Responds to observed attribute changes after render.
     * @param {string} name
     * @param {string|null} oldValue
     * @param {string|null} newValue
     */
    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered) return;
        this.processAttribute(name, oldValue, newValue);
    }

    /**
     * Applies behavior tied to supported attributes.
     * @param {string} name
     * @returns {void}
     */
    processAttribute(name) {
        if (name === 'disabled') {
            this.setAttribute('aria-disabled', this.hasAttribute('disabled') ? 'true' : 'false');
        } else if (name === 'divider') {
            const divider = this.shadowRoot?.querySelector('hr');
            if (divider) divider.hidden = !this.hasAttribute('divider');
        }
    }

    /**
     * Attaches item-level event listeners.
     * @returns {void}
     */
    setEventListeners() {
        this.handleClick = this.handleClick.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.addEventListener('click', this.handleClick);
        this.addEventListener('keydown', this.handleKeyDown);
    }

    /**
     * Removes item-level event listeners.
     * @returns {void}
     */
    removeEventListeners() {
        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('keydown', this.handleKeyDown);
    }

    /**
     * Blocks interaction when the menu item is disabled.
     * @param {MouseEvent} event
     * @returns {void}
     */
    handleClick(event) {
        if (this.hasAttribute('disabled')) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
        }
        const isToggle = event.target?.closest?.('neptune-checkbox, input[type="checkbox"], neptune-switch, neptune-radio-button');
        if (isToggle) return;
        this.dispatchEvent(new CustomEvent('menu-item-select', {
            bubbles: true,
            composed: true,
            detail: { item: this }
        }));
    }

    /**
     * Finds the first interactive descendant used for activation/focus.
     * @returns {Element|null}
     */
    findFirstActivatable() {
        return this.querySelector('neptune-button:not([disabled]), a[href], button:not([disabled]), [role="button"], neptune-checkbox, input:not([disabled])');
    }

    /**
     * Handles keyboard interactions for navigation and activation.
     * @param {KeyboardEvent} event
     * @returns {void}
     */
    handleKeyDown(event) {
        if (this.hasAttribute('disabled')) return;

        if (NAV_KEYS.includes(event.key)) {
            event.preventDefault();
            this.dispatchEvent(new CustomEvent('neptune-menu-navigate', {
                bubbles: true,
                composed: true,
                detail: { key: event.key, sourceItem: this }
            }));
            return;
        }

        if (event.key === 'Enter' || event.key === ' ') {
            const activatable = this.findFirstActivatable();
            if (activatable) {
                event.preventDefault();
                activatable.click?.();
            }
        }
    }

    /**
     * Focuses the best available interactive target inside the menu item.
     * @param {FocusOptions} [options]
     * @returns {void}
     */
    focus(options) {
        const activatable = this.findFirstActivatable();
        if (activatable) {
            const inner = activatable.shadowRoot?.querySelector('button:not([disabled]), a[href], input:not([disabled])');
            (inner ?? activatable).focus(options);
            return;
        }
        HTMLElement.prototype.focus.call(this, options);
    }
}

customElements.define('neptune-menu-item', MenuItem);
export default MenuItem;